import { ChangeDetectionStrategy, Component, computed, forwardRef } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';
import { v4 as uuid } from 'uuid';
import { ColumnsBlock, ContentBlockType } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { COLUMN_NODE_NAME } from './columns.model';

@Component({
  selector: 'dojo-columns',
  standalone: true,
  imports: [TiptapNodeViewContentDirective, StopPropagationDirective, MatTooltipModule],
  templateUrl: './columns.component.html',
  styleUrls: ['./columns.component.scss'],
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => ColumnsComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnsComponent extends NodeViewComponent<ColumnsBlock> {
  readonly isEditable = computed(() => this.editor().isEditable);
  readonly customCss = this.getProp('custom_css');
  readonly showBorders = this.getProp('show_borders');

  readonly columnCount = computed(() => {
    const node = this.node();
    return node.childCount;
  });

  readonly canAddColumn = computed(() => this.columnCount() < 3);
  readonly canRemoveColumn = computed(() => this.columnCount() > 2);

  override getDefaults = (): ColumnsBlock => ({
    id: uuid(),
    type: ContentBlockType.Columns,
    columns: [],
  });

  toggleBorders(): void {
    this.updateProp('show_borders', !this.showBorders());
  }

  addColumn(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();

    if (node.childCount >= 3) {
      return;
    }

    const insertPos = pos + node.nodeSize - 1;
    editor
      .chain()
      .insertContentAt(insertPos, {
        type: COLUMN_NODE_NAME,
        content: [{ type: 'paragraph' }],
      })
      .run();
  }

  removeLastColumn(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();

    if (node.childCount <= 2) {
      return;
    }

    const lastChild = node.child(node.childCount - 1);
    const lastChildPos = pos + node.nodeSize - 1 - lastChild.nodeSize;
    const { tr } = editor.state;
    tr.delete(lastChildPos, lastChildPos + lastChild.nodeSize);
    editor.view.dispatch(tr);
  }
}
